import type { Edicion, EdicionCiudadPublicacion } from '../../database/database';

/**
 * Estado tal como puede venir de BD o formularios: minúsculas, sin tildes (p. ej. catálogo → catalogo).
 */
export function normalizeEdicionEstadoPublico(estado: string | null | undefined): string {
  return String(estado ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** YYYY-MM-DD del calendario local (sin desfase UTC). */
export function todayYmdLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function haLlegadoYmd(fecha: string | null | undefined): boolean {
  if (fecha == null || String(fecha).trim() === '') return false;
  const ymd = String(fecha).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
  return ymd <= todayYmdLocal();
}

/**
 * Propuestas de una ciudad visibles al público en el catálogo de la edición:
 * interruptor manual ON, o ya llegó la fecha de publicación programada.
 */
export function isEdicionCiudadPropuestasPublicas(
  cfg: Pick<EdicionCiudadPublicacion, 'habilitado' | 'fecha_publicacion'> | null | undefined
): boolean {
  if (!cfg) return false;
  if (cfg.habilitado === true) return true;
  return haLlegadoYmd(cfg.fecha_publicacion);
}

/** Config de publicación de una ciudad dentro de la edición (por id de ciudad). */
export function getEdicionCiudadPublicacion(
  edicion: Pick<Edicion, 'ciudades_publicacion'>,
  ciudadId: string | number | null | undefined
): EdicionCiudadPublicacion | null {
  if (ciudadId == null || String(ciudadId).trim() === '') return null;
  const id = String(ciudadId);
  return edicion.ciudades_publicacion?.find((c) => String(c.ciudad_id) === id) ?? null;
}

export function isEdicionCiudadIdPropuestasPublicas(
  edicion: Pick<Edicion, 'ciudades_publicacion' | 'ciudades_ids'>,
  ciudadId: string | number | null | undefined
): boolean {
  if (ciudadId == null || String(ciudadId).trim() === '') return false;
  const cfg = getEdicionCiudadPublicacion(edicion, ciudadId);
  // Sin datos de publicación cargados (p. ej. migración pendiente): no ocultar de golpe.
  if (!cfg) {
    const pub = edicion.ciudades_publicacion;
    if (!pub || pub.length === 0) return true;
    return false;
  }
  return isEdicionCiudadPropuestasPublicas(cfg);
}

/**
 * True si ya llegó el día de anuncio (la fecha de anuncio es hoy o anterior).
 * Comparación en ISO YYYY-MM-DD.
 */
export function haLlegadoFechaAnuncio(fecha_anuncio: string | null | undefined): boolean {
  return haLlegadoYmd(fecha_anuncio);
}

/** Igual que anuncio, para fecha de catálogo. */
export function haLlegadoFechaCatalogo(fecha_catalogo: string | null | undefined): boolean {
  return haLlegadoYmd(fecha_catalogo);
}

/** Hay fecha de anuncio elegida (cualquier día) o ya pasó el día configurado. */
export function esCondicionAnuncioParaPublico(fecha_anuncio: string | null | undefined): boolean {
  if (fecha_anuncio != null && String(fecha_anuncio).trim() !== '') return true;
  return haLlegadoFechaAnuncio(fecha_anuncio);
}

/** Hay fecha de catálogo elegida o ya pasó el día configurado. */
export function esCondicionCatalogoParaPublico(fecha_catalogo: string | null | undefined): boolean {
  if (fecha_catalogo != null && String(fecha_catalogo).trim() !== '') return true;
  return haLlegadoFechaCatalogo(fecha_catalogo);
}

/**
 * En web pública: solo "planificada" queda oculta (equipo interno). Resto visible en listados.
 */
export function isEdicionVisibleEnWebPublica(edicion: Pick<Edicion, 'estado'>): boolean {
  return normalizeEdicionEstadoPublico(edicion.estado) !== 'planificada';
}

/** Habilita el botón hacia el catálogo (`/edicion/:nombre_link` o id numérico legacy): catalogo, activa o finalizada. */
export function isVerDetallesEdicionHabilitado(edicion: Pick<Edicion, 'estado'>): boolean {
  const st = normalizeEdicionEstadoPublico(edicion.estado);
  return st === 'catalogo' || st === 'activa' || st === 'finalizada';
}

/** Texto del CTA cuando la edición está anunciada pero el detalle aún no está disponible. */
export function etiquetaBotonVerDetalleEdicion(
  edicion: Pick<Edicion, 'estado'>,
  etiquetaPorDefecto: string
): string {
  if (normalizeEdicionEstadoPublico(edicion.estado) === 'anunciada') return 'Próximamente';
  return etiquetaPorDefecto;
}

/**
 * Permite ver el catálogo (página de propuestas) en web/app shell público.
 * Solo catalogo, activa o finalizada (reglas por estado, sin fechas).
 */
export function isEdicionCatalogoContenidoAccesiblePublico(edicion: Pick<Edicion, 'estado'>): boolean {
  const st = normalizeEdicionEstadoPublico(edicion.estado);
  return st === 'catalogo' || st === 'activa' || st === 'finalizada';
}

/** Votación (`/votar/:id`): edición activa, o con modo prueba activado. */
export function isVotacionPublicaPermitidaParaEdicion(
  edicion: Pick<Edicion, 'estado'> & { modo_prueba?: boolean }
): boolean {
  if (edicion.modo_prueba === true) return true;
  return normalizeEdicionEstadoPublico(edicion.estado) === 'activa';
}
